import { useState } from "react"
import { Check, Download, X } from "lucide-react"

import { DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Shell, Note, Body, Field, useBusy } from "./modalChrome"

// The send-back's request body, or null while the form is short of what the
// server needs. OrderActions::PlagiarismReviewed refuses a rejection without a
// denial_reason — which is why the old dialog's bare `{ approved: false }` only
// ever produced "The project is missing the rejection reason" (#489). A rescan
// reason returns the manuscript to the writer with the customer's requested
// changes, so those travel with it; "Other" carries the customer's own reason
// as messageReason. The reasons come from the payload (order.scanDenialReasons),
// never a client copy of the list. Pure so the rules are testable without
// driving the select.
export function scanSendBackPayload(reason, { message = "", other = "" } = {}) {
  if (!reason) return null
  const changes = message.trim()
  const ownReason = other.trim()
  if (reason.rescan && !changes) return null
  if (reason.other && !ownReason) return null

  return {
    approved: false,
    denialReason: reason.value,
    ...(reason.rescan ? { rejectionMessage: changes } : {}),
    ...(reason.other ? { messageReason: ownReason } : {}),
  }
}

// Review-scan dialog; mounted by OrderModals under "review-scan". Two steps:
// the verdict (download, approve, or start a send-back), then the send-back
// form the legacy modal had — the reason, the requested changes when that
// reason triggers a rescan, and a free-text reason for "Other".
export default function ReviewScanDialog({ order, runAction, onClose }) {
  const busy = useBusy()
  const sample = order.approvalFlow === "sample"
  // The report itself (deliverables.plagiarismReport, signed blob URL). The
  // dialog used to say "download it from the order" while no tab listed the
  // file (UPDA-86); it downloads from here and lives on the Document tab.
  const report = order.deliverables?.plagiarismReport
  const next = sample ? "approve to complete this sample" : "approve to move into editing"
  const reasons = order.scanDenialReasons ?? []
  const [sendingBack, setSendingBack] = useState(false)
  const [reasonValue, setReasonValue] = useState(reasons.length ? String(reasons[0].value) : "")
  const [message, setMessage] = useState("")
  const [other, setOther] = useState("")
  const reason = reasons.find((r) => String(r.value) === reasonValue)
  const payload = scanSendBackPayload(reason, { message, other })

  return (
    <Shell title="Review plagiarism scan" onClose={onClose}>
      <Body>
        Your originality &amp; AI report is ready. {report ? "Download it here" : "Download it from the Document tab"}, then {next}{" "}
        — or send it back with a reason if anything looks off.
      </Body>
      {report && (
        <div className="flex flex-wrap items-center gap-3">
          <Button asChild variant="outline" className="gap-[7px]">
            <a href={report.url} download={report.fileName}>
              <Download className="size-4" strokeWidth={2} />
              Download report
            </a>
          </Button>
          <span className="min-w-0 truncate text-[12px] text-muted-foreground">
            {report.fileName} · also on the Document tab
          </span>
        </div>
      )}
      {sendingBack ? (
        <>
          <Field label="Why are you sending it back?" htmlFor="scan-reason">
            <Select value={reasonValue} onValueChange={setReasonValue}>
              <SelectTrigger id="scan-reason" className="w-full">
                <SelectValue placeholder="Choose a reason" />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((r) => (
                  <SelectItem key={r.value} value={String(r.value)}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {reason?.other && (
            <Field label="Your reason" htmlFor="scan-other-reason">
              <Input
                id="scan-other-reason"
                aria-required="true"
                value={other}
                onChange={(e) => setOther(e.target.value)}
                placeholder="What's the concern?"
              />
            </Field>
          )}
          {reason?.rescan ? (
            <Field label="What needs to change?" htmlFor="scan-changes">
              <Textarea
                id="scan-changes"
                aria-required="true"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Describe the changes you're requesting. Reference line numbers from the Document tab, or use the Document Comments once you've sent it back."
              />
            </Field>
          ) : (
            reason && (
              <Note tone="muted">
                <b className="font-bold text-foreground">No new scan is needed —</b> your editor will review the manuscript
                and confirm the changes were made. Sending it back moves the order into editing.
              </Note>
            )
          )}
          <Note tone="red">
            <b className="font-bold text-primary">Plagiarism concerns only.</b> You could be responsible for additional scan
            costs if you send the report back for unrelated changes.
          </Note>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendingBack(false)} disabled={busy}>
              Back
            </Button>
            <Button
              className="gap-[7px] font-bold"
              disabled={!payload || busy}
              onClick={() => runAction("review_scan", payload)}
            >
              <X className="size-4" strokeWidth={2} />
              Send back
            </Button>
          </DialogFooter>
        </>
      ) : (
        <>
          <Note tone="muted">
            <b className="font-bold text-foreground">Heads up —</b> you have 48 hours to review. If no action is taken, the
            scan is approved automatically.
          </Note>
          <DialogFooter>
            <Button variant="outline" className="gap-[7px]" onClick={() => setSendingBack(true)} disabled={busy}>
              <X className="size-4" strokeWidth={2} />
              Send back
            </Button>
            <Button className="gap-[7px] font-bold" disabled={busy} onClick={() => runAction("review_scan", { approved: true })}>
              <Check className="size-4" strokeWidth={2.1} />
              Approve scan
            </Button>
          </DialogFooter>
        </>
      )}
    </Shell>
  )
}
